import { useCallback, useEffect, useState } from "react";
import {
  getNotificationLogs,
  sendNotification,
} from "../services/campaignService";

/**
 * 알림톡 템플릿 옵션
 */
const NOTIFICATION_TEMPLATES = [
  { value: "admin_tem1", label: "일정 조율 확인" },
  { value: "admin_tem2", label: "원고 작성 완료" },
  { value: "admin_tem3", label: "자료 보완 요청" },
  { value: "admin_tem4", label: "상담이 필요한 상품" },
  { value: "admin_tem6-1", label: "리워드용 상담사 연결" },
  { value: "admin_tem7", label: "미입금 추가 입금 요청" },
  { value: "admin_tem5", label: "리포트 업로드 완료" },
];

/**
 * 알림톡 발송 모달 컴포넌트
 * @param {Object} props
 * @param {boolean} props.isOpen - 모달 표시 여부
 * @param {number} props.adCampaignMetaId - 캠페인 메타 ID
 * @param {number} props.userId - 사용자 ID
 * @param {string} props.productName - 상품명
 * @param {string} props.submittedAt - 캐랭 접수시간
 * @param {number} props.purchaseAmount - 구매 금액
 * @param {Function} props.onClose - 닫기 핸들러
 * @param {Function} props.onSuccess - 발송 성공 핸들러
 */
function NotificationModal({
  isOpen,
  adCampaignMetaId,
  userId,
  productName,
  submittedAt,
  purchaseAmount,
  onClose,
  onSuccess,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);

  // 발송 로그 조회
  const fetchLogs = useCallback(async () => {
    if (!adCampaignMetaId) return;

    setLoading(true);
    setError(null);

    try {
      const logsData = await getNotificationLogs(adCampaignMetaId);
      setLogs(logsData || []);
    } catch (err) {
      console.error("알림 발송 로그 조회 실패:", err);
      setError("발송 이력을 불러오는데 실패했습니다.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [adCampaignMetaId]);

  // 모달이 열릴 때 로그 조회
  useEffect(() => {
    if (isOpen && adCampaignMetaId) {
      fetchLogs();
      setSelectedTemplate("");
      setConfirmed(false);
      setError(null);
    }
  }, [isOpen, adCampaignMetaId, fetchLogs]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // 알림 발송
  const handleSend = async () => {
    if (!selectedTemplate || !confirmed) return;

    setSending(true);
    setError(null);

    try {
      await sendNotification({
        adCampaignMetaId,
        templateCode: selectedTemplate,
        userId,
        productName,
        submittedAt,
        purchaseAmount,
      });

      // 발송 성공 후 로그 다시 조회
      await fetchLogs();

      // 상태 초기화
      setSelectedTemplate("");
      setConfirmed(false);

      if (onSuccess) {
        onSuccess();
      }

      alert("알림톡이 발송되었습니다.");
    } catch (err) {
      console.error("알림 발송 실패:", err);
      setError(err.message || "알림 발송에 실패했습니다.");
    } finally {
      setSending(false);
    }
  };

  // 발송하기 버튼 활성화 조건
  const canSend = selectedTemplate && confirmed && !sending;

  // 템플릿 레이블 가져오기
  const getTemplateLabel = (templateCode) => {
    const template = NOTIFICATION_TEMPLATES.find(
      (t) => t.value === templateCode,
    );
    return template ? template.label : templateCode;
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(2);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content notification-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "450px",
          width: "90%",
          padding: "24px",
          borderRadius: "12px",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600 }}>
            알림톡 발송
          </h3>
          <button
            className="modal-close-btn"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            ✕
          </button>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div
            style={{
              padding: "10px",
              marginBottom: "15px",
              backgroundColor: "#fee2e2",
              color: "#dc2626",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {/* 템플릿 선택 */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              color: "#4b5563",
            }}
          >
            알림톡 템플릿
          </label>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
              backgroundColor: "white",
              cursor: "pointer",
            }}
          >
            <option value="">템플릿 선택</option>
            {NOTIFICATION_TEMPLATES.map((template) => (
              <option key={template.value} value={template.value}>
                {template.label}
              </option>
            ))}
          </select>
        </div>

        {/* 발송 이력 */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              color: "#4b5563",
            }}
          >
            발송 이력
          </label>
          <div
            style={{
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              backgroundColor: "#f9fafb",
              padding: "12px",
              minHeight: "80px",
              maxHeight: "150px",
              overflowY: "auto",
            }}
          >
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                로딩 중...
              </div>
            ) : logs.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: "14px",
                }}
              >
                발송 이력이 없습니다.
              </div>
            ) : (
              <div style={{ fontSize: "14px" }}>
                {logs.map((log, index) => (
                  <div
                    key={log.id || index}
                    style={{
                      marginBottom: index < logs.length - 1 ? "8px" : 0,
                      color: "#374151",
                    }}
                  >
                    {index + 1}. {getTemplateLabel(log.templateCode)} 안내 [
                    {formatDate(log.sentAt)}]
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 확인 체크박스 */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontSize: "14px",
              color: "#4b5563",
            }}
          >
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{
                width: "16px",
                height: "16px",
                margin: 0,
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
            <span style={{ lineHeight: "16px" }}>
              발송된 알림톡은 취소할 수 없습니다.
            </span>
          </label>
        </div>

        {/* 발송 버튼 */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="notification-send-btn"
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: canSend ? "#1f2937" : "#9ca3af",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "15px",
            fontWeight: 500,
            cursor: canSend ? "pointer" : "not-allowed",
            transition: "background-color 0.2s",
          }}
        >
          {sending ? "발송 중..." : "발송하기"}
        </button>
      </div>
    </div>
  );
}

export default NotificationModal;
